import React, { useState } from 'react';
import { noop } from 'src/components/base';
import * as S from './styles';

export interface TextInputProps {
  name: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  label?: string;
  defaultValue?: string;
  value?: string;
  disabled?: boolean;
  type?: 'text' | 'password' | 'email' | 'number';
  placeholder?: string;
  tabIndex?: number;
  helper?: React.ReactNode;
  warning?: boolean;
  className?: string;
  children?: React.ReactNode;
  cancelButton?: React.ReactNode;
  inputRef?: React.RefObject<HTMLInputElement>;
  required?: boolean;
  customInputProps?: object;
  minLength?: number;
  maxLength?: number;
  variant?: 'default' | 'rounded';
  autoFocus?: boolean;
  ariaLabel?: string;
  autoComplete?: string;
  onInvalid?: (e: React.InvalidEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  showCounter?: boolean;
}

const TextInput = ({
  name,
  onChange = noop,
  onKeyUp = noop,
  label,
  disabled = false,
  defaultValue,
  value,
  type = 'text',
  placeholder = undefined,
  tabIndex = 0,
  helper = null,
  warning = false,
  className = undefined,
  children = null,
  inputRef,
  required = false,
  minLength,
  maxLength,
  customInputProps = {},
  variant = 'default',
  autoFocus,
  autoComplete,
  ariaLabel,
  onInvalid,
  cancelButton,
  onClick,
  showCounter,
  ...otherProps
}: TextInputProps) => {
  const [characterCount, setCharacterCount] = useState<number>(
    defaultValue?.length || value?.length || 0
  );

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCharacterCount(event.target.value.length);
    onChange(event);
  };

  return (
    <S.InputWrapper className={className}>
      {/* eslint-disable-next-line jsx-a11y/label-has-for */}
      <S.Label htmlFor={name}>
        <S.LabelText>{label}</S.LabelText>
        {cancelButton}
        {children}
      </S.Label>

      <S.Input
        autoFocus={autoFocus}
        warning={warning}
        required={required}
        name={name}
        id={name}
        type={type}
        defaultValue={defaultValue}
        value={value}
        placeholder={placeholder}
        tabIndex={tabIndex}
        disabled={disabled}
        onChange={handleOnChange}
        onKeyUp={onKeyUp}
        ref={inputRef}
        minLength={minLength}
        maxLength={maxLength}
        variant={variant}
        aria-label={ariaLabel}
        autoComplete={autoComplete}
        onInvalid={onInvalid}
        onClick={onClick}
        {...customInputProps}
        {...otherProps}
      />

      {helper && (
        <S.HelperWrapper>
          <S.Helper warning={warning}>{helper}</S.Helper>
          {showCounter && maxLength && (
            <S.CharCounter>{`${characterCount} / ${maxLength}`}</S.CharCounter>
          )}
        </S.HelperWrapper>
      )}
    </S.InputWrapper>
  );
};

export default TextInput;
